import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../services/api';

const ReadingProgressContext = createContext();

export const useReadingProgress = () => {
  const context = useContext(ReadingProgressContext);
  if (!context) {
    throw new Error('useReadingProgress must be used within a ReadingProgressProvider');
  }
  return context;
};

export const ReadingProgressProvider = ({ children }) => {
  const [sessionId] = useState(() => {
    // Get or create session ID
    let session = localStorage.getItem('autism-education-session');
    if (!session) {
      session = 'session-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('autism-education-session', session);
    }
    return session;
  });
  
  const [readingProgress, setReadingProgress] = useState({});
  const [moduleProgress, setModuleProgress] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReadingProgress();
  }, [sessionId]);

  const loadReadingProgress = async () => {
    try {
      setLoading(true);
      const [progressData, moduleProgressData] = await Promise.all([
        apiService.getReadingProgress(sessionId),
        apiService.getModuleProgressSummary(sessionId)
      ]);
      
      setReadingProgress(progressData.progress || {});
      
      // Convert module progress array to object for easier lookup
      const moduleProgressObj = {};
      moduleProgressData.moduleProgress.forEach(progress => {
        moduleProgressObj[progress.moduleId] = progress;
      });
      setModuleProgress(moduleProgressObj);
      
    } catch (error) {
      console.error('Error loading reading progress:', error);
      // Initialize with empty progress if loading fails
      setReadingProgress({});
      setModuleProgress({});
    } finally {
      setLoading(false);
    }
  };

  const markSectionAsRead = async (moduleId, sectionIndex, isRead = true) => {
    try {
      // Optimistically update local state
      setReadingProgress(prev => ({
        ...prev,
        [moduleId]: {
          ...prev[moduleId],
          [sectionIndex]: {
            isRead,
            readAt: new Date().toISOString()
          }
        }
      }));

      // Update backend
      await apiService.updateReadingProgress(sessionId, moduleId, sectionIndex, isRead);
      
      // Refresh module progress summary
      const moduleProgressData = await apiService.getModuleProgressSummary(sessionId);
      const moduleProgressObj = {};
      moduleProgressData.moduleProgress.forEach(progress => {
        moduleProgressObj[progress.moduleId] = progress;
      });
      setModuleProgress(moduleProgressObj);
      
    } catch (error) {
      console.error('Error updating reading progress:', error);
      // Revert optimistic update on error
      loadReadingProgress();
    }
  };

  const isSectionRead = (moduleId, sectionIndex) => {
    return readingProgress[moduleId]?.[sectionIndex]?.isRead || false;
  };

  const getModuleReadingProgress = (moduleId) => {
    return moduleProgress[moduleId] || {
      totalSections: 0,
      sectionsRead: 0,
      progressPercentage: 0
    };
  };

  const value = {
    sessionId,
    readingProgress,
    moduleProgress,
    loading,
    markSectionAsRead,
    isSectionRead,
    getModuleReadingProgress,
    loadReadingProgress
  };

  return (
    <ReadingProgressContext.Provider value={value}>
      {children}
    </ReadingProgressContext.Provider>
  );
};